/** URL base de la API derivada del host actual. */
function resolveHostApiUrl(): string {
  if (typeof window !== 'undefined' && window.location?.origin) {
    return `${window.location.origin}/api/v1`;
  }

  if (typeof location !== 'undefined' && location.origin) {
    return `${location.origin}/api/v1`;
  }

  return '/api/v1';
}

function isIpHost(hostname: string): boolean {
  return /^\d{1,3}(\.\d{1,3}){3}$/.test(hostname) || hostname.includes(':');
}

/**
 * Detecta si el hostname actual corresponde a un tenant.
 * Si no se detecta tenant, se usa VITE_API_URL como fallback.
 */
function hasTenantInHost(): boolean {
  if (typeof window === 'undefined' || !window.location?.hostname) {
    return false;
  }

  const hostname = window.location.hostname.toLowerCase();
  if (!hostname) return false;

  // Casos locales válidos de tenants en desarrollo (ej: public.localhost, localhost)
  if (hostname === 'localhost' || hostname.endsWith('.localhost')) {
    return true;
  }

  // IP normalmente no representa resolución de tenant por dominio
  if (isIpHost(hostname)) {
    return false;
  }

  const parts = hostname.split('.').filter(Boolean);
  if (parts.length >= 3) {
    const reservedSubdomains = new Set(['www', 'app', 'api']);
    return !reservedSubdomains.has(parts[0] ?? '');
  }

  // Dominio propio de cliente (tenant con dominio primario)
  return parts.length >= 2;
}

function resolveConfiguredApiUrl(): string {
  const windowUrl = window.__APP_CONFIG__?.apiBaseUrl?.trim();
  const importMetaUrl = import.meta.env?.VITE_API_URL?.trim();
  const processUrl =
    typeof process !== 'undefined' ? process.env.VITE_API_URL?.trim() : '';

  if (hasTenantInHost()) {
    return resolveHostApiUrl();
  }

  const configuredUrl = windowUrl || importMetaUrl || processUrl;

  if (!configuredUrl) {
    return resolveHostApiUrl();
  }

  return configuredUrl;
}

const configuredApiUrl = resolveConfiguredApiUrl();

function trimTrailingSlash(value: string): string {
  return value.replace(/\/+$/, '');
}

function stripApiPrefix(path: string): string {
  return path.replace(/^\/api\/v1(?=\/|$)/, '');
}

const normalizedApiUrl = trimTrailingSlash(configuredApiUrl);
const inferredAppUrl = normalizedApiUrl.replace(/\/api\/v1$/, '');

export const API_BASE_URL: string = normalizedApiUrl;
export const APP_BASE_URL: string = inferredAppUrl;

// ── Almacenamiento de tokens ─────────────────────────────────────────────────

const ACCESS_KEY = 'auth_access';
const REFRESH_KEY = 'auth_refresh';

export function getAccessToken(): string | null {
  return localStorage.getItem(ACCESS_KEY);
}

export function getRefreshToken(): string | null {
  return localStorage.getItem(REFRESH_KEY);
}

export function setTokens(access: string, refresh: string): void {
  localStorage.setItem(ACCESS_KEY, access);
  localStorage.setItem(REFRESH_KEY, refresh);
}

export function setAccessToken(access: string): void {
  localStorage.setItem(ACCESS_KEY, access);
}

export function clearTokens(): void {
  localStorage.removeItem(ACCESS_KEY);
  localStorage.removeItem(REFRESH_KEY);
}

// ── Refresco automático del access token ─────────────────────────────────────

let _refreshPromise: Promise<string> | null = null;

async function refreshAccessToken(): Promise<string> {
  if (_refreshPromise) return _refreshPromise;

  _refreshPromise = (async () => {
    const refresh = getRefreshToken();
    if (!refresh) throw new Error('No refresh token disponible');

    const res = await fetch(buildUrl('/api/v1/token/refresh/'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refresh }),
    });

    if (!res.ok) {
      clearTokens();
      throw new Error('Sesión expirada. Por favor inicie sesión de nuevo.');
    }

    const json = (await res.json()) as { access: string };
    setAccessToken(json.access);
    return json.access;
  })().finally(() => {
    _refreshPromise = null;
  });

  return _refreshPromise;
}

// ── Utilidades ───────────────────────────────────────────────────────────────

export type QueryParams = Record<
  string,
  string | number | boolean | undefined | null
>;

function buildUrl(path: string, params?: QueryParams): string {
  const normalizedPath = path.startsWith('/health')
    ? path
    : stripApiPrefix(path);
  const baseUrl = normalizedPath.startsWith('/health')
    ? APP_BASE_URL
    : API_BASE_URL;
  const url = new URL(`${baseUrl}${normalizedPath}`);
  if (params) {
    for (const [key, value] of Object.entries(params)) {
      if (value !== undefined && value !== null) {
        url.searchParams.set(key, String(value));
      }
    }
  }
  return url.toString();
}

// ── Función principal de petición ────────────────────────────────────────────

interface RequestOptions {
  body?: unknown;
  params?: QueryParams;
  /** Si true no agrega el header Authorization */
  skipAuth?: boolean;
}

async function request<T>(
  method: string,
  path: string,
  options: RequestOptions = {},
): Promise<T> {
  const { body, params, skipAuth = false } = options;

  const doFetch = async (token: string | null): Promise<Response> => {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };
    if (token && !skipAuth) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    return fetch(buildUrl(path, params), {
      method,
      headers,
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
  };

  let token = skipAuth ? null : getAccessToken();
  let res = await doFetch(token);

  // Intento único de refresco ante un 401
  if (res.status === 401 && !skipAuth) {
    try {
      token = await refreshAccessToken();
      res = await doFetch(token);
    } catch {
      // El refresco falló; se propagará el error del cuerpo original
    }
  }

  let json: unknown;
  try {
    json = await res.json();
  } catch {
    throw new Error(`Error HTTP ${res.status}: ${res.statusText}`);
  }

  if (!res.ok) {
    // Propaga el envelope de error para que el llamador pueda leer .message
    throw json;
  }

  return json as T;
}

// ── Cliente exportado ────────────────────────────────────────────────────────

export const apiClient = {
  get<T>(path: string, params?: QueryParams, skipAuth = false): Promise<T> {
    return request<T>('GET', path, { params, skipAuth });
  },
  post<T>(
    path: string,
    body?: unknown,
    params?: QueryParams,
    skipAuth = false,
  ): Promise<T> {
    return request<T>('POST', path, { body, params, skipAuth });
  },
  put<T>(path: string, body?: unknown, params?: QueryParams): Promise<T> {
    return request<T>('PUT', path, { body, params });
  },
  delete<T>(path: string): Promise<T> {
    return request<T>('DELETE', path, {});
  },
};
